import type { CSSProperties, ReactNode } from 'react';
import './PiecesDuCarnet.css';

/**
 * Les commandes du carnet au téléphone (chantier telephone-habit, 03, maquette
 * `app-au-telephone.html`, `curseur()`, `interrupteur()`, `.ligne-reg`) : plus aucune pastille.
 * Un oui ou non est un interrupteur qui glisse, trois ou quatre positions un curseur à crans, un
 * choix long une ligne qui ouvre sa liste. Le dessin seulement : ce qui se choisit, et ce que
 * fait un toucher, c'est l'écran qui le dit.
 */

/** Un cran : sa clé, son mot. */
export type Cran<T> = readonly [T, string];

/**
 * Un curseur à crans (`.curs`, `.cran`, `.bouton`), son titre au-dessus (`.rg-t`) : une glissière,
 * le bouton posé sur le cran choisi. `attribut` : le nom de l'attribut `data-` que porte chaque
 * cran, avec sa clé (les scripts de vérification et le tuto le cherchent).
 *
 * `mini` : le petit curseur du téléphone couché (`.curs.mini`, chantier telephone-couche), sur une
 * ligne, à la `largeur` que la maquette lui donne ; `nom` le nomme quand il n'a pas de titre.
 */
export function Curseur<T extends string | number | null>({
  titre,
  crans,
  choisi,
  onChoisir,
  attribut,
  tuto,
  mini = false,
  largeur,
  nom,
  sansCranPossible = false,
  attributs,
  children,
}: {
  titre?: string;
  crans: readonly Cran<T>[];
  choisi: T;
  onChoisir: (cle: T) => void;
  attribut?: string;
  tuto?: string;
  mini?: boolean;
  largeur?: number;
  nom?: string;
  /**
   * `choisi` peut n'être aucun des crans (un champ du Profil vide, ou écrit autrement à
   * l'ordinateur) : le bouton ne se pose alors nulle part. Sans, il se pose sur le premier.
   */
  sansCranPossible?: boolean;
  attributs?: Record<`data-${string}`, string>;
  /** Sous la glissière (la valeur d'un champ du Profil qui n'est aucun des crans). */
  children?: ReactNode;
}) {
  const trouve = crans.findIndex(([cle]) => cle === choisi);
  const rang = Math.max(0, trouve);
  const style = { ['--n' as string]: crans.length, ['--i' as string]: rang, width: largeur } as CSSProperties;
  return (
    <div className="pouce-reglage" data-tuto={tuto} {...attributs}>
      {titre && <p className="pouce-rg-t">{titre}</p>}
      <div className={`pouce-curs${mini ? ' pouce-curs--mini' : ''}`} role="radiogroup" aria-label={titre ?? nom} style={style}>
        {(trouve >= 0 || !sansCranPossible) && <span className="pouce-curs-bouton" aria-hidden="true" />}
        {crans.map(([cle, mot]) => (
          <button
            key={String(cle)}
            type="button"
            role="radio"
            aria-checked={cle === choisi}
            className={`pouce-cran${cle === choisi ? ' est-choisi' : ''}`}
            {...(attribut ? { [`data-${attribut}`]: cle === null ? 'aucun' : String(cle) } : {})}
            onClick={() => onChoisir(cle)}
          >
            {mot}
          </button>
        ))}
      </div>
      {children}
    </div>
  );
}

/** Un interrupteur (`.inter-l`, `.inter-s`) : le titre, sa phrase grise dessous, oui ou non d'un toucher. */
export function Interrupteur({
  titre,
  sous,
  oui,
  onBasculer,
}: {
  titre: string;
  sous?: string;
  oui: boolean;
  onBasculer: (oui: boolean) => void;
}) {
  return (
    <button type="button" role="switch" aria-checked={oui} className="pouce-inter-l" onClick={() => onBasculer(!oui)}>
      <span className="pouce-inter-mots">
        <b>{titre}</b>
        {sous && <small>{sous}</small>}
      </span>
      <span className={`pouce-inter-s${oui ? ' est-oui' : ''}`} aria-hidden="true">
        <i />
      </span>
    </button>
  );
}

/** Une ligne qui ouvre sa liste (`.ligne-reg`) : « Projet  Tous › ». */
export function LigneQuiOuvre({
  titre,
  valeur,
  ouverte,
  onOuvrir,
  attributs,
}: {
  titre: string;
  valeur: ReactNode;
  ouverte?: boolean;
  onOuvrir: () => void;
  attributs?: Record<`data-${string}`, string>;
}) {
  return (
    <button
      type="button"
      className="pouce-ligne-reg"
      aria-expanded={ouverte}
      aria-label={typeof valeur === 'string' ? `${titre} : ${valeur}` : undefined}
      onClick={onOuvrir}
      {...attributs}
    >
      {titre}
      <span>{valeur} ›</span>
    </button>
  );
}

/**
 * La liste qu'ouvre une ligne : une ligne par choix, le choisi coché. `attribut` comme pour le
 * curseur.
 */
export function ListeDeChoix<T extends string | number | null>({
  nom,
  choix,
  choisi,
  onChoisir,
  attribut,
}: {
  nom: string;
  choix: readonly (readonly [T, ReactNode])[];
  choisi: T;
  onChoisir: (cle: T) => void;
  attribut?: string;
}) {
  return (
    <div className="pouce-liste" role="radiogroup" aria-label={nom}>
      {choix.map(([cle, mot]) => (
        <button
          key={String(cle)}
          type="button"
          role="radio"
          aria-checked={cle === choisi}
          className={`pouce-liste-ligne${cle === choisi ? ' est-choisie' : ''}`}
          {...(attribut ? { [`data-${attribut}`]: cle === null ? 'aucun' : String(cle) } : {})}
          onClick={() => onChoisir(cle)}
        >
          <span className="pouce-liste-mot">{mot}</span>
          {/* La coche est dessinée (PiecesDuCarnet.css) : le texte du choix reste son seul mot. */}
          <span className="pouce-liste-coche" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
